// billing/RecordPaymentModal.tsx
import React, { useState, useEffect } from 'react';
import {
    X, DollarSign, Calendar, CreditCard, CheckCircle,
    Banknote, FileText, Receipt, Building2
} from 'lucide-react';
import { useBillingStore } from '../../store/billingStore';
import { Invoice } from '../../services/billing.service';

interface RecordPaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    invoice: Invoice | null;
}

const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({ isOpen, onClose, invoice }) => {
    const { recordPayment } = useBillingStore();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [formData, setFormData] = useState({
        amount: 0,
        paymentMethod: 'bank_transfer',
        transactionId: '',
        transactionDate: new Date().toISOString().split('T')[0],
        notes: '',
        sendReceipt: true,
    });

    const paymentMethods = [
        { value: 'bank_transfer', label: 'Bank Transfer', icon: Building2 },
        { value: 'wire', label: 'Wire Transfer', icon: Banknote },
        { value: 'paypal', label: 'PayPal', icon: CreditCard },
        { value: 'stripe', label: 'Stripe', icon: CreditCard },
        { value: 'credit_card', label: 'Credit Card', icon: CreditCard },
        { value: 'check', label: 'Check', icon: FileText },
        { value: 'cash', label: 'Cash', icon: Banknote },
        { value: 'other', label: 'Other', icon: Receipt },
    ];

    useEffect(() => {
        if (invoice) {
            setFormData(prev => ({
                ...prev,
                amount: invoice.amountDue,
                transactionDate: new Date().toISOString().split('T')[0],
            }));
            setErrors({});
        }
    }, [invoice]);

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.amount || formData.amount <= 0) {
            newErrors.amount = 'Amount must be greater than 0';
        }
        if (invoice && formData.amount > invoice.amountDue) {
            newErrors.amount = `Amount cannot exceed amount due ($${invoice.amountDue.toFixed(2)})`;
        }
        if (!formData.transactionDate) {
            newErrors.transactionDate = 'Transaction date is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!invoice || !validateForm()) return;

        console.log('Recording payment for invoice:', { id: invoice.id, _id: (invoice as any)._id, invoiceNumber: invoice.invoiceNumber });
        const invoiceId = invoice.id || (invoice as any)._id;

        setIsSubmitting(true);
        try {
            const dto: any = {
                invoiceId: invoiceId,
                amount: formData.amount,
                paymentMethod: formData.paymentMethod,
                transactionDate: new Date(formData.transactionDate).toISOString(),
                sendReceipt: formData.sendReceipt,
            };
            if (formData.transactionId) {
                dto.transactionId = formData.transactionId;
            }
            if (formData.notes) {
                dto.notes = formData.notes;
            }
            await recordPayment(dto);
            onClose();
        } catch (error: any) {
            console.error('Failed to record payment:', error);
            console.error('Error response:', error.response?.data);
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: invoice?.currency || 'USD',
        }).format(amount);
    };

    if (!isOpen || !invoice) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
                {/* Backdrop */}
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

                {/* Modal */}
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-xl">
                                <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Record Payment
                            </h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-5">
                        {/* Invoice Summary Card */}
                        <div className="p-4 bg-gradient-to-r from-primary-50 to-indigo-50 dark:from-primary-900/20 dark:to-indigo-900/20 rounded-xl border border-primary-100 dark:border-primary-800">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Invoice</p>
                                    <p className="font-semibold text-gray-900 dark:text-white">{invoice.invoiceNumber}</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{invoice.billTo.companyName}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Amount Due</p>
                                    <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                                        {formatCurrency(invoice.amountDue)}
                                    </p>
                                </div>
                            </div>
                            {invoice.amountPaid > 0 && (
                                <div className="mt-3 pt-3 border-t border-primary-200 dark:border-primary-700">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600 dark:text-gray-400">Already Paid</span>
                                        <span className="font-medium text-green-600 dark:text-green-400">
                                            {formatCurrency(invoice.amountPaid)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Amount Input */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Amount Received <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="number"
                                    value={formData.amount}
                                    onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                                    min={0}
                                    step={0.01}
                                    max={invoice.amountDue}
                                    className={`w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border rounded-xl text-gray-900 dark:text-white text-lg font-medium focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 ${errors.amount ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-600'
                                        }`}
                                />
                            </div>
                            {errors.amount && <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.amount}</p>}
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, amount: invoice.amountDue })}
                                className="mt-1 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                            >
                                Pay full amount ({formatCurrency(invoice.amountDue)})
                            </button>
                        </div>

                        {/* Payment Method Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Payment Method <span className="text-red-500">*</span>
                            </label>
                            <div className="grid grid-cols-4 gap-2">
                                {paymentMethods.slice(0, 4).map(method => (
                                    <button
                                        key={method.value}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, paymentMethod: method.value })}
                                        className={`flex flex-col items-center gap-1 px-3 py-3 rounded-xl text-xs font-medium transition-colors border ${formData.paymentMethod === method.value
                                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 border-primary-500'
                                            : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                                            }`}
                                    >
                                        <method.icon className="w-4 h-4" />
                                        <span className="truncate">{method.label}</span>
                                    </button>
                                ))}
                            </div>
                            <select
                                value={formData.paymentMethod}
                                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                                className="w-full mt-2 px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                            >
                                {paymentMethods.map(method => (
                                    <option key={method.value} value={method.value}>{method.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Transaction Date */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Transaction Date <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="date"
                                    value={formData.transactionDate}
                                    onChange={(e) => setFormData({ ...formData, transactionDate: e.target.value })}
                                    className={`w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 ${errors.transactionDate ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-600'
                                        }`}
                                />
                            </div>
                            {errors.transactionDate && <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.transactionDate}</p>}
                        </div>

                        {/* Transaction Reference */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Transaction Reference
                            </label>
                            <input
                                type="text"
                                value={formData.transactionId}
                                onChange={(e) => setFormData({ ...formData, transactionId: e.target.value })}
                                placeholder="e.g., TXN123456789"
                                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                            />
                        </div>

                        {/* Notes */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Notes
                            </label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                rows={2}
                                placeholder="Any additional notes about this payment..."
                                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                            />
                        </div>

                        {/* Send Receipt Toggle */}
                        <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                            <input
                                type="checkbox"
                                checked={formData.sendReceipt}
                                onChange={(e) => setFormData({ ...formData, sendReceipt: e.target.checked })}
                                className="w-5 h-5 text-primary-600 rounded-md border-gray-300 dark:border-gray-600 focus:ring-primary-500"
                            />
                            <div>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Send receipt to client</span>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Email receipt to {invoice.billTo.email}</p>
                            </div>
                        </label>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={isSubmitting || formData.amount <= 0}
                            className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Recording...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="w-4 h-4" />
                                    Confirm Payment
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecordPaymentModal;
